import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Trash2, Edit, Handshake } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';

const ALL_STATUSES = [
  { value: 'commandée', label: 'Commandée' },
  { value: 'impression', label: 'Impression' },
  { value: 'couture', label: 'Couture' },
  { value: 'livraison bureau', label: 'Livraison bureau' },
  { value: 'en cours de livraison client', label: 'En cours de livraison client' },
  { value: 'livrée', label: 'Livrée' }
];

const PARTNER_TYPES = [
  { value: 'imprimeur', label: 'Imprimeur' },
  { value: 'couture', label: 'Couture' },
  { value: 'transport', label: 'Transport' },
  { value: 'manager', label: 'Manager' }
];

const DEFAULT_STATUSES = {
  imprimeur: ['impression', 'couture'],
  couture: ['couture', 'livraison bureau'],
  transport: ['livraison bureau', 'en cours de livraison client'],
  manager: []
};

export const Partners = () => {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    partner_type: 'imprimeur',
    allowed_statuses: ['impression', 'couture']
  });
  const [editStatuses, setEditStatuses] = useState([]);

  useEffect(() => {
    fetchPartners();
  }, []);

  const fetchPartners = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/partners`,
        { withCredentials: true }
      );
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/partners`,
        formData,
        { withCredentials: true }
      );
      toast.success('Partenaire ajouté avec succès');
      setCreateOpen(false);
      resetForm();
      fetchPartners();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'ajout");
    }
  };

  const handleEdit = (partner) => {
    setEditingPartner(partner);
    setEditStatuses(partner.allowed_statuses || []);
    setEditOpen(true);
  };

  const handleUpdateStatuses = async () => {
    try {
      await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/partners/${editingPartner.id}`,
        { allowed_statuses: editStatuses },
        { withCredentials: true }
      );
      toast.success('Statuts mis à jour');
      setEditOpen(false);
      fetchPartners();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce partenaire ?')) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/partners/${id}`,
        { withCredentials: true }
      );
      toast.success('Partenaire supprimé');
      fetchPartners();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', email: '', password: '', partner_type: 'imprimeur', allowed_statuses: ['impression', 'couture'] });
  };

  const handleTypeChange = (type) => {
    setFormData({
      ...formData,
      partner_type: type,
      allowed_statuses: DEFAULT_STATUSES[type] || []
    });
  };

  const toggleStatus = (status, list, setter) => {
    if (list.includes(status)) {
      setter(list.filter(s => s !== status));
    } else {
      setter([...list, status]);
    }
  };

  const getTypeLabel = (type) => {
    return PARTNER_TYPES.find(t => t.value === type)?.label || type;
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">Partenaires</h1>
            <p className="text-sm text-slate-600 mt-1">Gestion des accès partenaires (Imprimeur, Couture, Transport)</p>
          </div>

          <Dialog open={createOpen} onOpenChange={(open) => { setCreateOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-partner-button" className="bg-slate-900 hover:bg-slate-800">
                <Plus size={18} className="mr-2" />
                Nouveau partenaire
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nouveau partenaire</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nom*</Label>
                  <Input
                    data-testid="partner-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    placeholder="Nom du partenaire"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email*</Label>
                    <Input
                      data-testid="partner-email-input"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Mot de passe*</Label>
                    <Input
                      data-testid="partner-password-input"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      required
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Type de partenaire*</Label>
                  <Select value={formData.partner_type} onValueChange={handleTypeChange}>
                    <SelectTrigger data-testid="partner-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PARTNER_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Statuts autorisés</Label>
                  <div className="flex flex-wrap gap-2">
                    {ALL_STATUSES.map(s => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => toggleStatus(s.value, formData.allowed_statuses, (val) => setFormData({ ...formData, allowed_statuses: val }))}
                        className={`px-3 py-1 rounded-md text-xs border transition-colors ${
                          formData.allowed_statuses.includes(s.value)
                            ? 'bg-slate-900 text-white border-slate-900'
                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                        }`}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500">Le 1er statut = ce que le partenaire voit, les suivants = ce qu'il peut appliquer</p>
                </div>
                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
                  <Button data-testid="submit-partner-button" type="submit" className="bg-slate-900 hover:bg-slate-800">Créer</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Handshake size={20} className="text-slate-600" />
              <CardTitle className="text-lg font-medium">Liste des partenaires</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
              </div>
            ) : partners.length === 0 ? (
              <p className="text-center py-8 text-sm text-slate-500">Aucun partenaire configuré</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-medium">Nom</TableHead>
                    <TableHead className="font-medium">Email</TableHead>
                    <TableHead className="font-medium">Type</TableHead>
                    <TableHead className="font-medium">Statuts autorisés</TableHead>
                    <TableHead className="font-medium">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {partners.map((p) => (
                    <TableRow key={p.id} data-testid="partner-row" className="hover:bg-slate-50">
                      <TableCell className="font-medium">{p.name}</TableCell>
                      <TableCell>{p.email}</TableCell>
                      <TableCell>
                        <Badge className="bg-slate-100 text-slate-800 border border-slate-200">
                          {getTypeLabel(p.partner_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {(p.allowed_statuses || []).map(s => (
                            <Badge key={s} className="bg-blue-50 text-blue-700 border border-blue-200 text-xs">
                              {ALL_STATUSES.find(st => st.value === s)?.label || s}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            data-testid="edit-partner-button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(p)}
                            className="h-8 w-8 p-0"
                          >
                            <Edit size={16} />
                          </Button>
                          <Button
                            data-testid="delete-partner-button"
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(p.id)}
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifier les statuts - {editingPartner?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Statuts autorisés</Label>
                <div className="flex flex-wrap gap-2">
                  {ALL_STATUSES.map(s => (
                    <button
                      key={s.value}
                      type="button"
                      onClick={() => toggleStatus(s.value, editStatuses, setEditStatuses)}
                      className={`px-3 py-1 rounded-md text-xs border transition-colors ${
                        editStatuses.includes(s.value)
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-500">Le 1er statut = commandes visibles, les suivants = statuts applicables</p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setEditOpen(false)}>Annuler</Button>
                <Button data-testid="save-partner-statuses" onClick={handleUpdateStatuses} className="bg-slate-900 hover:bg-slate-800">
                  Enregistrer
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};
